import { Controller } from "@hotwired/stimulus"

// F10 link magnet — estimates ManyChat's per-contact bill vs. our flat price.
// Directional only; ManyChat's tiers change. Flat price comes from the server
// (data-manychat-calculator-flat-cents-value) so it tracks the real plan.
//
// Deep-linkable + shareable: ?contacts=N prefills the input on load, and the
// URL is kept in sync (via replaceState) as the user types so the current
// state can be copied/shared.
export default class extends Controller {
  static targets = ["contacts", "manychat", "flat", "savings", "copyLink"]
  static values = { flatCents: Number }

  DEFAULT_CONTACTS = 2000

  connect() {
    this.prefillFromUrl()
    this.recompute()
  }

  prefillFromUrl() {
    const params = new URLSearchParams(window.location.search)
    const raw = params.get("contacts")
    if (raw === null) return

    const contacts = parseInt(raw, 10)
    if (Number.isInteger(contacts) && contacts > 0 && String(contacts) === raw.trim()) {
      this.contactsTarget.value = contacts
    }
  }

  recompute() {
    const contacts = Math.max(0, parseInt(this.contactsTarget.value || "0", 10))
    const flat = (this.flatCentsValue || 1500) / 100
    const manychat = this.estimateManychat(contacts)

    this.manychatTarget.textContent = this.money(manychat)
    this.flatTarget.textContent = this.money(flat)
    const savings = Math.max(0, (manychat - flat) * 12)
    this.savingsTarget.textContent = this.money(savings)

    this.syncUrl(contacts)
  }

  syncUrl(contacts) {
    const url = new URL(window.location.href)
    if (contacts > 0 && contacts !== this.DEFAULT_CONTACTS) {
      url.searchParams.set("contacts", contacts)
    } else {
      url.searchParams.delete("contacts")
    }
    window.history.replaceState(window.history.state, "", url)
  }

  shareUrl() {
    const contacts = Math.max(0, parseInt(this.contactsTarget.value || "0", 10))
    const url = new URL(window.location.href)
    if (contacts > 0 && contacts !== this.DEFAULT_CONTACTS) {
      url.searchParams.set("contacts", contacts)
    } else {
      url.searchParams.delete("contacts")
    }
    return url.toString()
  }

  async copyLink() {
    const url = this.shareUrl()

    try {
      await navigator.clipboard.writeText(url)
    } catch (_err) {
      this.fallbackCopy(url)
    }

    this.flashCopied()
  }

  fallbackCopy(text) {
    const textarea = document.createElement("textarea")
    textarea.value = text
    textarea.style.position = "fixed"
    textarea.style.opacity = "0"
    document.body.appendChild(textarea)
    textarea.focus()
    textarea.select()
    try {
      document.execCommand("copy")
    } finally {
      document.body.removeChild(textarea)
    }
  }

  flashCopied() {
    if (!this.hasCopyLinkTarget) return

    if (this.copyResetTimeout) clearTimeout(this.copyResetTimeout)

    if (this.originalCopyLabel === undefined) {
      this.originalCopyLabel = this.copyLinkTarget.textContent
    }

    this.copyLinkTarget.textContent = "Link copied ✓"
    this.copyResetTimeout = setTimeout(() => {
      this.copyLinkTarget.textContent = this.originalCopyLabel
    }, 2000)
  }

  // Rough public-tier model: ~$15 base to 500 contacts, then ~$12 per extra 500.
  estimateManychat(contacts) {
    if (contacts <= 500) return 15
    const extraBlocks = Math.ceil((contacts - 500) / 500)
    return 15 + extraBlocks * 12
  }

  money(n) {
    return "$" + Math.round(n).toLocaleString()
  }
}
